import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';
import { EDUCATION_CATEGORIES, EDUCATION_ARTICLES } from '../data/education';

const LearnPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = activeCategory === 'all'
    ? EDUCATION_ARTICLES
    : EDUCATION_ARTICLES.filter((a) => a.category === activeCategory);

  const getCategoryLabel = (id) => EDUCATION_CATEGORIES.find((c) => c.id === id)?.label || '';

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Education
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="learn-heading"
          >
            The Library
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            {EDUCATION_ARTICLES.length} guides to deepen your understanding.
            Knowledge is the foundation of every great experience.
          </motion.p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3" data-testid="category-filters">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-5 py-2 font-sans text-xs tracking-[0.15em] uppercase transition-all duration-300 ${
                activeCategory === 'all'
                  ? 'bg-white text-black'
                  : 'border border-white/10 text-zinc-400 hover:border-white/25 hover:text-white'
              }`}
            >
              All ({EDUCATION_ARTICLES.length})
            </button>
            {EDUCATION_CATEGORIES.map((cat) => {
              const count = EDUCATION_ARTICLES.filter((a) => a.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  data-testid={`filter-${cat.id}`}
                  className={`px-5 py-2 font-sans text-xs tracking-[0.15em] uppercase transition-all duration-300 ${
                    activeCategory === cat.id
                      ? 'bg-white text-black'
                      : 'border border-white/10 text-zinc-400 hover:border-white/25 hover:text-white'
                  }`}
                >
                  {cat.label} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Category Description */}
      {activeCategory !== 'all' && (
        <section className="px-6 md:px-12 lg:px-24 pb-8">
          <motion.p
            key={activeCategory}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center font-sans text-sm text-zinc-500 italic"
          >
            {EDUCATION_CATEGORIES.find((c) => c.id === activeCategory)?.description}
          </motion.p>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-8 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((article, index) => (
              <motion.div
                key={article.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.5) }}
                onClick={() => navigate(`/learn/${article.slug}`)}
                className="group cursor-pointer border border-white/5 p-6 hover:border-white/15 transition-all duration-300"
                data-testid={`article-card-${article.slug}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600">
                    {getCategoryLabel(article.category)}
                  </p>
                  <p className="font-sans text-[10px] text-zinc-700">{article.readTime}</p>
                </div>
                <h3 className="font-serif text-xl text-zinc-100 mb-2 group-hover:text-white transition-colors">
                  {article.title}
                </h3>
                <p className="font-sans text-xs text-zinc-500 leading-relaxed">
                  {article.subtitle}
                </p>
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="font-sans text-[10px] tracking-[0.15em] uppercase text-zinc-600 group-hover:text-zinc-400 transition-colors">
                    Read guide
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-8 text-center">
          <div>
            <p className="font-serif text-3xl text-zinc-50 mb-1">{EDUCATION_ARTICLES.length}</p>
            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600">Guides</p>
          </div>
          <div>
            <p className="font-serif text-3xl text-zinc-50 mb-1">{EDUCATION_CATEGORIES.length}</p>
            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600">Categories</p>
          </div>
          <div>
            <p className="font-serif text-3xl text-zinc-50 mb-1">
              {EDUCATION_ARTICLES.reduce((sum, a) => sum + parseInt(a.readTime), 0)}
            </p>
            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600">Min of Reading</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default LearnPage;
